import React, { useEffect, useMemo } from 'react';

import get from 'lodash/get';
import isNil from 'lodash/isNil';

import { Grid, Stack, ToggleButton, Help } from '@gogame/components';
import { FormField, withFormFieldGroupFormField } from '@gogame/form';
import { dateFNS } from '@gogame/intl-ts';

import {
  MODIFY_ACTION,
  ACTION_TYPE,
} from 'scenes/SmartRules/SmartRuleForm/ModifyAction';

import {
  SCHEDULE,
  RECURRENCE,
  REST_DAY_AFTER_ACTION,
  REST_DAY_CROSS_RULE,
  DAY,
  WEEK,
  HOURS,
  ONCE,
  AT_TIME,
  ON_DATE,
  DAYS,
  EVERY,
} from './constants';
import { OnDateFiled, EveryHoursField, AtTimeField, DaysField } from './fields';
import { scheduleSchema } from './schemas';

export const Schedule = withFormFieldGroupFormField({
  name: SCHEDULE,
  validationSchema: scheduleSchema,
})(({ value, formik }) => {
  const {
    [RECURRENCE]: recurrence,
    [REST_DAY_AFTER_ACTION]: restDayAfterAction,
    [REST_DAY_CROSS_RULE]: restDayCrossRule,
    [DAYS]: repeatDays,
  } = value || {};
  const { [ACTION_TYPE]: actionTypeId } =
    get(formik.values, MODIFY_ACTION) || {};

  const showRestDayAfterAction = useMemo(() => {
    return actionTypeId && recurrence !== ONCE && recurrence !== HOURS;
  }, [actionTypeId, recurrence]);

  const showRestDayCrossRule = useMemo(() => {
    return actionTypeId && recurrence !== ONCE;
  }, [actionTypeId, recurrence]);

  useEffect(() => {
    if (showRestDayAfterAction && isNil(restDayAfterAction)) {
      formik.setFieldValue(`${SCHEDULE}.${REST_DAY_AFTER_ACTION}`, 7);
    }
  }, [showRestDayAfterAction, restDayAfterAction]);

  useEffect(() => {
    if (showRestDayCrossRule && !restDayCrossRule) {
      formik.setFieldValue(`${SCHEDULE}.${REST_DAY_CROSS_RULE}`, 0);
    }
  }, [showRestDayCrossRule, restDayCrossRule]);

  useEffect(() => {
    if (recurrence !== HOURS && !value?.[AT_TIME]) {
      let offset = new Date().getMinutes() % 5;

      const nextTick = dateFNS.addMinutes(new Date(), 10 - offset);

      const hours = nextTick.getHours();
      const minutes = nextTick.getMinutes();

      formik.setFieldValue(
        `${SCHEDULE}.${AT_TIME}`,
        `${('0' + hours).slice(-2)}:${('0' + minutes).slice(-2)}`
      );
    }
  }, [recurrence, value?.[AT_TIME]]);

  useEffect(() => {
    if (recurrence === ONCE && !value?.[ON_DATE]) {
      let date = dateFNS.startOfDay(new Date());

      formik.setFieldValue(`${SCHEDULE}.${ON_DATE}`, date);
    }
  }, [recurrence, value?.[ON_DATE]]);

  useEffect(() => {
    if (recurrence === HOURS && !value?.[EVERY]) {
      formik.setFieldValue(`${SCHEDULE}.${EVERY}`, 1);
    }
  }, [recurrence, value?.[EVERY]]);

  useEffect(() => {
    if (recurrence !== WEEK && repeatDays) {
      formik.setFieldValue(`${SCHEDULE}.${DAYS}`, undefined);
    }
  }, [recurrence, repeatDays]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Stack direction={'row'} spacing={2} alignItems={'center'}>
          <Stack>
            <FormField.ToggleButtonGroup name={RECURRENCE} padding={'0'}>
              <ToggleButton value={HOURS}>Hourly</ToggleButton>
              <ToggleButton value={DAY}>Daily</ToggleButton>
              <ToggleButton value={WEEK}>Weekly</ToggleButton>
              <ToggleButton value={ONCE}>Once</ToggleButton>
            </FormField.ToggleButtonGroup>
          </Stack>
          {recurrence === ONCE ? <OnDateFiled /> : null}
          {recurrence === HOURS ? <EveryHoursField /> : <AtTimeField />}
        </Stack>
      </Grid>

      {recurrence === WEEK ? (
        <Grid container item xs={12}>
          <DaysField />
        </Grid>
      ) : null}

      {showRestDayAfterAction ? (
        <Grid item container xs={4}>
          <Stack
            direction={'row'}
            spacing={1}
            alignItems={'center'}
            flex={'1 0 auto'}
          >
            <Stack flex={'1 0 auto'}>
              <FormField.TextField
                label={'Resting days after action taken'}
                name={REST_DAY_AFTER_ACTION}
                type={'number'}
                padding={0}
                _cleanOnUnmount={true}
              />
            </Stack>
            <Help>
              The amount of the days after conditions met that will be skipped
              before next run
            </Help>
          </Stack>
        </Grid>
      ) : null}

      {showRestDayCrossRule ? (
        <Grid item container xs={4}>
          <Stack
            direction={'row'}
            spacing={1}
            alignItems={'center'}
            flex={'1 0 auto'}
          >
            <Stack flex={'1 0 auto'}>
              <FormField.TextField
                label={'Cross-rule resting days'}
                name={REST_DAY_CROSS_RULE}
                type={'number'}
                padding={0}
                _cleanOnUnmount={true}
              />
            </Stack>
            <Help>
              Put ‘on hold’ all the modified entities, so that other rules can’t
              change the.
            </Help>
          </Stack>
        </Grid>
      ) : null}
    </Grid>
  );
});
